import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const { language } = useLanguage();
  
  const faqs = language === 'ES' ? [
    {
      question: "¿Qué es ListingPro AI?",
      answer: "ListingPro AI es una aplicación web completa diseñada para agentes de bienes raíces que transforma listados de propiedades en paquetes de marketing completos. Sube tus fotos y detalles de la propiedad, y nuestra IA genera descripciones MLS, subtítulos para redes sociales, textos de email, SMS y más. Además, incluye datos de mercado en tiempo real, generador de actualizaciones de mercado y gestión de contactos — todo en minutos."
    },
    {
      question: "¿Qué funciones están incluidas?",
      answer: "ListingPro AI incluye: Mejora de fotos con IA, Generador de descripciones MLS, Subtítulos para Instagram y Facebook, Marketing por Email y SMS, Contenido bilingüe (EN/ES), Datos de mercado en tiempo real, Generador de actualizaciones de mercado con IA, Gestión de contactos con importación CSV, Perfil de agente con marca personal, Creación de imágenes para redes sociales, y más."
    },
    {
      question: "¿Qué es la función de Datos de Mercado?",
      answer: "La función de Datos de Mercado te proporciona información en tiempo real sobre el mercado inmobiliario, incluyendo valores medios de viviendas, tasas hipotecarias, días en el mercado e inventario. Puedes usar estos datos para crear actualizaciones de mercado personalizadas con tu marca que puedes compartir con tus clientes por email o redes sociales."
    },
    {
      question: "¿Cómo funciona el Generador de Actualizaciones de Mercado?",
      answer: "El generador de actualizaciones de mercado utiliza IA para crear contenido de marketing profesional basado en los datos actuales del mercado. Genera automáticamente un email completo, publicaciones para redes sociales cortas y largas, y puntos clave — todo con tu información de contacto y marca de corretaje incluidos."
    },
    {
      question: "¿Qué es la Gestión de Contactos?",
      answer: "La función de gestión de contactos te permite mantener una lista de tus clientes y prospectos. Puedes agregar contactos manualmente o importarlos desde un archivo CSV. Esto facilita tener todos tus contactos organizados para cuando quieras enviar actualizaciones de mercado o campañas de marketing."
    },
    {
      question: "¿Qué es el Perfil de Agente?",
      answer: "El perfil de agente te permite configurar tu información profesional incluyendo tu foto, logo de corretaje, teléfono y enlaces a redes sociales. Esta información se incluye automáticamente en las actualizaciones de mercado que generas, dándote una marca profesional consistente."
    },
    {
      question: "¿ListingPro AI se integra directamente con MLS?",
      answer: "No, ListingPro AI no se integra directamente con sistemas MLS en la Versión 1. En cambio, proporcionamos contenido listo para MLS que puedes copiar con un clic y pegar directamente en tu sistema MLS. Esto funciona con cualquier plataforma MLS sin integraciones complejas."
    },
    {
      question: "¿Puedo publicar directamente en Instagram o Facebook?",
      answer: "En la Versión 1, no ofrecemos publicación directa a redes sociales. Sin embargo, generamos subtítulos optimizados para Instagram y Facebook que puedes copiar y usar al publicar manualmente."
    },
    {
      question: "¿Qué idiomas están soportados?",
      answer: "ListingPro AI soporta salida en inglés y español. Puedes elegir generar contenido solo en inglés, solo en español, o ambos idiomas para cada listado. Esto es perfecto para agentes bilingües o mercados con clientes hispanohablantes."
    },
    {
      question: "¿Cómo funciona la prueba gratuita?",
      answer: "Cada nuevo usuario obtiene una prueba gratuita de 7 días con acceso completo a todas las funciones. No se requiere tarjeta de crédito para comenzar. Después de 7 días, necesitarás suscribirte para continuar creando nuevos listados."
    },
    {
      question: "¿Qué cuenta como un 'listado activo'?",
      answer: "Un listado activo es cualquier listado con un estado diferente a 'Archivado'. Esto incluye listados en Borrador, Activo, Pendiente y Vendido. Una vez que archivas un listado, ya no cuenta para tu límite."
    },
    {
      question: "¿Cómo funciona la mejora de fotos?",
      answer: "Nuestra mejora de fotos con IA mejora tus fotos de listado ajustando brillo, contraste y claridad, además de aplicar un ligero enfoque. Está diseñado para hacer que tus fotos luzcan más profesionales sin manipulación pesada. Las fotos originales se preservan."
    },
    {
      question: "¿El contenido generado cumple con MLS?",
      answer: "Sí, nuestra IA está diseñada para generar descripciones profesionales estilo MLS que evitan violaciones de vivienda justa y lenguaje discriminatorio. Sin embargo, siempre recomendamos revisar el contenido antes de enviar para asegurar que cumpla con los requisitos específicos de tu MLS."
    },
    {
      question: "¿Cuánto cuesta ListingPro AI?",
      answer: "ListingPro AI cuesta $20 USD como pago único. Esto te da acceso de por vida a todas las funciones, incluyendo listados ilimitados, mejora de fotos con IA, generación de contenido, datos de mercado y más. Sin pagos mensuales ni cargos recurrentes."
    }
  ] : [
    {
      question: "What is ListingPro AI?",
      answer: "ListingPro AI is a comprehensive web application designed for real estate agents that transforms property listings into complete marketing packages. Upload your photos and property details, and our AI generates MLS remarks, social media captions, email copy, SMS text, and more. Plus, it includes real-time market data, market update generator, and contact management — all in minutes."
    },
    {
      question: "What features are included?",
      answer: "ListingPro AI includes: AI Photo Enhancement, MLS Remarks Generator, Instagram & Facebook Captions, Email & SMS Marketing, Bilingual Content (EN/ES), Real-Time Market Data Insights, AI Market Update Generator, Contact Management with CSV Import, Agent Profile with Branding, Social Media Image Creator, and more."
    },
    {
      question: "What is the Market Data feature?",
      answer: "The Market Data feature provides you with real-time real estate market insights including median home values, mortgage rates, days on market, and inventory levels. You can use this data to create branded market updates that you can share with your clients via email or social media."
    },
    {
      question: "How does the Market Update Generator work?",
      answer: "The market update generator uses AI to create professional marketing content based on current market data. It automatically generates a complete email, short and long social media posts, and key takeaways — all with your contact information and brokerage branding included."
    },
    {
      question: "What is Contact Management?",
      answer: "The contact management feature allows you to maintain a list of your clients and prospects. You can add contacts manually or import them from a CSV file. This makes it easy to have all your contacts organized for when you want to send market updates or marketing campaigns."
    },
    {
      question: "What is the Agent Profile?",
      answer: "The agent profile allows you to set up your professional information including your headshot, brokerage logo, phone number, and social media links. This information is automatically included in the market updates you generate, giving you consistent professional branding."
    },
    {
      question: "Does ListingPro AI integrate directly with MLS?",
      answer: "No, ListingPro AI does not integrate directly with MLS systems in Version 1. Instead, we provide MLS-ready content that you can copy with one click and paste directly into your MLS system. This works with any MLS platform without complex integrations."
    },
    {
      question: "Can I post directly to Instagram or Facebook?",
      answer: "In Version 1, we don't offer direct posting to social media platforms. However, we generate optimized captions for Instagram and Facebook that you can copy and use when posting manually."
    },
    {
      question: "What languages are supported?",
      answer: "ListingPro AI supports English and Spanish output. You can choose to generate content in English only, Spanish only, or both languages for each listing. This is perfect for bilingual agents or markets with Spanish-speaking clients."
    },
    {
      question: "How much does ListingPro AI cost?",
      answer: "ListingPro AI costs $20 USD as a one-time payment. This gives you lifetime access to all features, including unlimited listings, AI photo enhancement, content generation, market data, and more. No monthly fees or recurring charges."
    },
    {
      question: "What counts as an 'active listing'?",
      answer: "An active listing is any listing with a status other than 'Archived'. This includes Draft, Active, Pending, and Sold listings. Once you archive a listing, it no longer counts toward your limit, freeing up space for new listings."
    },
    {
      question: "How does photo enhancement work?",
      answer: "Our AI photo enhancement improves your listing photos by adjusting brightness, contrast, and clarity, plus applying mild sharpening. It's designed to make your photos look more professional without heavy manipulation. Original photos are preserved."
    },
    {
      question: "Is the generated content MLS-compliant?",
      answer: "Yes, our AI is designed to generate professional, MLS-style descriptions that avoid fair housing violations and discriminatory language. However, we always recommend reviewing content before submitting to ensure it meets your specific MLS requirements."
    },
    {
      question: "Do I need to pay monthly?",
      answer: "No! ListingPro AI is a one-time payment of $20. Once you pay, you have lifetime access to all features with no recurring charges. We believe in simple, transparent pricing."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-foreground mb-4">
            {language === 'ES' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {language === 'ES' ? 'Todo lo que necesitas saber sobre ListingPro AI' : 'Everything you need to know about ListingPro AI'}
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border border-border rounded-xl px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-semibold hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
            {language === 'ES' ? '¿Aún tienes preguntas?' : 'Still have questions?'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {language === 'ES' ? 'Nuestro equipo está aquí para ayudarte. Contáctanos cuando quieras.' : 'Our team is here to help. Reach out anytime.'}
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
          >
            {language === 'ES' ? 'Contactar Soporte' : 'Contact Support'}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
